import { ComponentType, FormContainerType } from '@lemonade-hq/cdk';
import { EN_GB_LOCALE, EN_US_LOCALE } from '@lemonade-hq/lemonation';
import type { PartialBy } from '@lemonade-hq/ts-helpers';
import { ABC, randomString } from '@lemonade-hq/ts-helpers';
import type { UniclientInlineFormSkipAction } from '../..';
import type { UniclientWizardComponentBase } from '../types';
import type { FormElementCreatorProps } from './common';
import type {
  CreditCardProvider,
  FormAddressElementControlledConfigurableProps,
  FormAutocompleteElementControlledConfigurableProps,
  FormBankTransferACHElementControlledConfigurableProps,
  FormButtonElementControlledConfigurableProps,
  FormCheckboxElementControlledConfigurableProps,
  FormChipsElementControlledConfigurableProps,
  FormConsentCheckboxElementControlledConfigurableProps,
  FormCopyToClipboardElementControlledConfigurableProps,
  FormCounterElementControlledConfigurableProps,
  FormCreditCardElementControlledConfigurableProps,
  FormDatePickerElementControlledConfigurableProps,
  FormInputTextElementControlledConfigurableProps,
  FormLabelElementControlledConfigurableProps,
  FormMapElementControlledConfigurableProps,
  FormPhoneElementControlledConfigurableProps,
  FormRadioElementControlledConfigurableProps,
  FormRatingElementControlledConfigurableProps,
  FormSelectElementControlledConfigurableProps,
  FormSignatureTextElementControlledConfigurableProps,
  FormSliderElementControlledConfigurableProps,
  FormSwitchElementControlledConfigurableProps,
  FormTextAreaElementControlledConfigurableProps,
} from './composers';
import { CHIPS_CURRENT_ITEM_ATTRIBUTE, CREDIT_CARD_ATTRIBUTES, SEARCH_ATTRIBUTE } from './composers';
import type { BaseFormProps, FormRowProps, InlineBaseFormProps } from './form.types';

export type FormProps<TAttribute extends string = string> = BaseFormProps<TAttribute> &
  UniclientWizardComponentBase<'form'>;
export type InlineFormProps<TAttribute extends string = string> = InlineBaseFormProps<TAttribute> &
  UniclientWizardComponentBase<'inlineForm'>;

export function form<TAttribute extends string = string>(
  props: Omit<FormProps<TAttribute>, 'type'>,
): FormProps<TAttribute> {
  return { ...props, type: 'form' };
}

export function inlineForm<TAttribute extends string = string>(
  props: Omit<InlineFormProps<TAttribute>, 'type'>,
): InlineFormProps<TAttribute> {
  return { ...props, type: 'inlineForm' };
}

export function inlineFormSkip(props?: Omit<UniclientInlineFormSkipAction, 'type'>): UniclientInlineFormSkipAction {
  return { ...props, type: 'inlineFormSkip' };
}

export type FormRowAdditionalProps = {
  readonly marginBottomSize?: 'lg' | 'md' | 'none' | 'sm' | 'xl';
};

export function row<TAttribute extends string = string>({
  weights: explicitWeights,
  marginBottomSize,
  ...restProps
}: FormRowAdditionalProps & PartialBy<FormRowProps<TAttribute>, 'type' | 'weights'>): FormRowAdditionalProps &
  FormRowProps<TAttribute> {
  const weights = explicitWeights ?? restProps.elements.map(() => 100 / restProps.elements.length);
  return {
    ...restProps,
    ...(marginBottomSize != null && { additionalProps: { marginBottomSize } }),
    weights,
    type: FormContainerType.Row,
  };
}

export function column<TAttribute extends string = string>(
  props: Omit<FormRowProps<TAttribute>, 'type' | 'weights'>,
): FormRowProps<TAttribute> {
  return { ...props, weights: [100], type: FormContainerType.Column };
}

function checkbox<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormCheckboxElementControlledConfigurableProps<TAttribute>>,
): FormCheckboxElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Checkbox };
}

function copyToClipboard<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormCopyToClipboardElementControlledConfigurableProps<TAttribute>>,
): FormCopyToClipboardElementControlledConfigurableProps<TAttribute> {
  return {
    ...props,
    elementType: ComponentType.CopyToClipboard,
  };
}

function text<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormInputTextElementControlledConfigurableProps<TAttribute>>, 'type'>,
): FormInputTextElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Input, type: 'text' };
}

function number<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormInputTextElementControlledConfigurableProps<TAttribute>>, 'type'> & {
    readonly inputMode?: 'decimal' | 'numeric' | 'tel';
  },
): FormInputTextElementControlledConfigurableProps<TAttribute> {
  // type is `text` as safari and firefox allow non-numeric chars for input type number without invoking onChange
  return { inputMode: 'numeric', ...props, elementType: ComponentType.Input, type: 'text' };
}

function currency<TAttribute extends string = string>({
  currency: currencyOverride,
  region: regionOverride,
  ...props
}: FormElementCreatorProps<FormInputTextElementControlledConfigurableProps<TAttribute>>['formatCurrencyProps'] &
  Omit<
    FormElementCreatorProps<FormInputTextElementControlledConfigurableProps<TAttribute>>,
    'formatCurrency' | 'type'
  >): Omit<FormInputTextElementControlledConfigurableProps<TAttribute>, 'formatCurrencyProps'> & {
  readonly formatCurrencyProps: Partial<
    FormElementCreatorProps<FormInputTextElementControlledConfigurableProps<TAttribute>>['formatCurrencyProps']
  >;
} {
  return {
    ...props,
    formatCurrency: true,
    formatCurrencyProps: { currency: currencyOverride, region: regionOverride },
    elementType: ComponentType.Input,
    type: 'text',
  };
}

function email<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormInputTextElementControlledConfigurableProps<TAttribute>>, 'type'>,
): FormInputTextElementControlledConfigurableProps<TAttribute> {
  return {
    ...props,
    elementType: ComponentType.Input,
    type: 'email',
    inputMode: 'email',
  };
}

function password<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormInputTextElementControlledConfigurableProps<TAttribute>>, 'type'>,
): FormInputTextElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Input, type: 'password' };
}

function address<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormAddressElementControlledConfigurableProps<TAttribute>>, 'type'>,
): FormAddressElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Address };
}

function map<TAttribute extends string = string>(
  props: Omit<FormMapElementControlledConfigurableProps<TAttribute>, 'elementType'>,
): FormMapElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Map };
}

function select<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormSelectElementControlledConfigurableProps<TAttribute>>,
): FormSelectElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Select };
}

function autocomplete<TAttribute extends string = string>({
  keysToEncodeOnInterpolation = [],
  ...props
}: FormElementCreatorProps<
  FormAutocompleteElementControlledConfigurableProps<TAttribute>
>): FormAutocompleteElementControlledConfigurableProps<TAttribute> {
  return {
    ...props,
    elementType: ComponentType.Autocomplete,
    keysToEncodeOnInterpolation: [...keysToEncodeOnInterpolation, 'itemsUrl'],
  };
}

function date<TAttribute extends string = string>(
  props: Omit<
    FormElementCreatorProps<FormDatePickerElementControlledConfigurableProps<TAttribute>>,
    'preferredInteraction'
  >,
): FormDatePickerElementControlledConfigurableProps<TAttribute> {
  const { locale, ...restProps } = props;
  return {
    ...restProps,
    elementType: ComponentType.DatePicker,
    preferredInteraction: 'keyboard',
    ...(locale != null && { locale: setLocale(locale) }),
  };
}

function datePicker<TAttribute extends string = string>(
  props: Omit<
    FormElementCreatorProps<FormDatePickerElementControlledConfigurableProps<TAttribute>>,
    'keyboardOnly' | 'preferredInteraction'
  >,
): FormDatePickerElementControlledConfigurableProps<TAttribute> {
  const { locale, ...restProps } = props;
  return {
    ...restProps,
    elementType: ComponentType.DatePicker,
    preferredInteraction: 'picker',
    ...(locale != null && { locale: setLocale(locale) }),
  };
}

function counter<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormCounterElementControlledConfigurableProps<TAttribute>>,
): FormCounterElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.MinusPlus };
}

function label<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormLabelElementControlledConfigurableProps<TAttribute>>,
): FormLabelElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Label, attribute: randomString(5, ABC) as unknown as TAttribute };
}

function toggle<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormSwitchElementControlledConfigurableProps<TAttribute>>,
): FormSwitchElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Switch };
}

function signature<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormSignatureTextElementControlledConfigurableProps<TAttribute>>, 'type'>,
): FormSignatureTextElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Signature, type: 'text' };
}

function chips<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormChipsElementControlledConfigurableProps<TAttribute>>,
): FormChipsElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.PillsInput };
}

function phone<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormPhoneElementControlledConfigurableProps<TAttribute>>, 'type'>,
): FormPhoneElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Phone };
}

function textArea<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormTextAreaElementControlledConfigurableProps<TAttribute>>,
): FormTextAreaElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.TextArea };
}

function creditCard<TProvider extends CreditCardProvider, TAttribute extends string = string>(
  props: Omit<
    FormElementCreatorProps<
      FormCreditCardElementControlledConfigurableProps<TAttribute> & {
        readonly provider: NoInfer<TProvider>;
      }
    > & { readonly attribute: TAttribute; readonly provider: TProvider },
    'children' | 'onChange'
  >,
): FormCreditCardElementControlledConfigurableProps<TAttribute> {
  return {
    ...props,
    elementType: ComponentType.CreditCard,
  } as FormCreditCardElementControlledConfigurableProps<TAttribute> & { readonly provider: TProvider };
}

function bankTransferACH<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormBankTransferACHElementControlledConfigurableProps<TAttribute>>,
): FormBankTransferACHElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.BankTransferACH };
}

function slider<TAttribute extends string = string>({
  keysToIgnoreOnInterpolation = [],
  changeOnRelease = false,
  ...props
}: FormElementCreatorProps<
  FormSliderElementControlledConfigurableProps<TAttribute>
>): FormSliderElementControlledConfigurableProps<TAttribute> {
  const ignoredKeys = changeOnRelease ? [...keysToIgnoreOnInterpolation, 'label'] : keysToIgnoreOnInterpolation;
  return {
    ...props,
    keysToIgnoreOnInterpolation: ignoredKeys,
    changeOnRelease,
    elementType: ComponentType.Slider,
  };
}

function consentCheckbox<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormConsentCheckboxElementControlledConfigurableProps<TAttribute>>,
): FormConsentCheckboxElementControlledConfigurableProps<TAttribute> {
  return {
    ...props,
    elementType: ComponentType.ConsentCheckbox,
  };
}

function radio<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormRadioElementControlledConfigurableProps<TAttribute>>,
): FormRadioElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.Radio };
}

function button<TAttribute extends string = string>(
  props: Omit<FormElementCreatorProps<FormButtonElementControlledConfigurableProps<TAttribute>>, 'attribute'>,
): FormButtonElementControlledConfigurableProps<TAttribute> {
  return { ...props, attribute: randomString(5, ABC) as unknown as TAttribute, elementType: ComponentType.Button };
}

function setLocale(locale: string): string {
  return locale === EN_US_LOCALE ? EN_US_LOCALE : locale.startsWith('en') ? EN_GB_LOCALE : locale;
}

function rating<TAttribute extends string = string>(
  props: FormElementCreatorProps<FormRatingElementControlledConfigurableProps<TAttribute>>,
): FormRatingElementControlledConfigurableProps<TAttribute> {
  return { ...props, elementType: ComponentType.StarRating };
}

export const input = {
  autocomplete: Object.assign(autocomplete, { searchAttribute: SEARCH_ATTRIBUTE }),
  address,
  checkbox,
  chips: Object.assign(chips, { currentItem: CHIPS_CURRENT_ITEM_ATTRIBUTE }),
  toggle,
  text,
  number,
  currency,
  email,
  password,
  map,
  select,
  date,
  datePicker,
  counter,
  label,
  signature,
  phone,
  textArea,
  slider,
  consentCheckbox,
  copyToClipboard,
  radio,
  creditCard: Object.assign(creditCard, { attributes: CREDIT_CARD_ATTRIBUTES }),
  bankTransferACH,
  button,
  rating,
};
